import { useDialogs } from '@paintscout/ui-business'
import type { Payment, QuoteDocument } from 'paintscout'
import { useCallback } from 'react'
import PaymentsDialog from './PaymentsDialog'
import ViewPaymentDialog from './ViewPaymentDialog'

export interface OpenPaymentDialogOptions {
  payment?: Payment
  quote: QuoteDocument
  paymentIndex?: number
  quoteId?: string
  onConfirm?: (quote: QuoteDocument) => void
  onCancel?: () => void
  onDelete?: () => void
  onQuoteUpdated?: (updatedQuote: QuoteDocument) => void
  backOnCancel?: boolean
  markingPaid?: boolean
  canDelete?: boolean
  hideDeposit?: boolean
  /** When opened outside a QuoteContext (e.g. the Deal summary) so the dialog saves via the mutation instead of context. */
  onDeal?: boolean
  onReceipt?: (payment: Payment) => void
  onRefund?: (payment: Payment) => void
  onSaveNote?: (updatedQuote: QuoteDocument) => void
  inPresentation?: boolean
}

export function useOpenPaymentDialog() {
  const { openDialog, dismissDialog } = useDialogs()

  return useCallback(
    ({
      payment,
      quote,
      paymentIndex,
      quoteId,
      onConfirm,
      onCancel,
      onDelete,
      onQuoteUpdated,
      backOnCancel,
      markingPaid,
      canDelete,
      hideDeposit,
      onReceipt,
      onRefund,
      onSaveNote,
      inPresentation,
      onDeal
    }: OpenPaymentDialogOptions) => {
      const handleCancel = onCancel ?? dismissDialog

      if (!payment || payment.status === 'pending') {
        openDialog(PaymentsDialog, {
          quote,
          paymentIndex,
          quoteId,
          backOnCancel,
          markingPaid,
          canDelete,
          hideDeposit,
          onDeal,
          onConfirm,
          onCancel: handleCancel
        })
      } else {
        openDialog(ViewPaymentDialog, {
          activityId: payment.activityId,
          payment,
          quote,
          onClose: handleCancel,
          onDelete,
          readonly: !canDelete,
          onQuoteUpdated,
          onReceipt,
          onRefund,
          onSaveNote,
          inPresentation
        })
      }
    },
    [openDialog, dismissDialog]
  )
}
